export type NavId =
  | 'dashboard'
  | 'members'
  | 'users'
  | 'offerings'
  | 'reports'
  | 'settings'
  | 'tutorials'
  | 'support';

export interface AppNavItem {
  id: NavId;
  /** Clave ngx-translate (SIDENAV.*) */
  labelKey: string;
  /** Línea breve bajo el nombre en los desplegables del menú superior (SIDENAV.DESC_*). */
  descriptionKey: string;
  faIcon: [string, string];
  primeIcon: string;
  route: string;
  allowedRoles: string[];
  /** Solo visible si los tutoriales están habilitados por misión o por iglesia. */
  requiresTutorials?: boolean;
}

export interface NavContext {
  roles: readonly string[];
  tutorialsEnabled: boolean;
}

/**
 * Fuente única del menú de la app de iglesia. La comparación de roles es exacta
 * (mismas cadenas que emiten los claims `roles` + `access`).
 */
export const APP_NAV_ITEMS: readonly AppNavItem[] = [
  {
    id: 'dashboard',
    labelKey: 'SIDENAV.MENU_DASHBOARD',
    descriptionKey: 'SIDENAV.DESC_DASHBOARD',
    faIcon: ['fas', 'gauge'],
    primeIcon: 'pi pi-home',
    route: '/dashboard/main',
    allowedRoles: ['Admin', 'Pastor', 'Liderazgo', 'Tesorería', 'Secretaría'],
  },
  {
    id: 'members',
    labelKey: 'SIDENAV.MENU_MEMBERS',
    descriptionKey: 'SIDENAV.DESC_MEMBERS',
    faIcon: ['fas', 'users'],
    primeIcon: 'pi pi-users',
    route: '/dashboard/members',
    allowedRoles: ['Admin', 'Pastor', 'Secretaría', 'access_membresia'],
  },
  {
    id: 'users',
    labelKey: 'SIDENAV.MENU_LEADERS',
    descriptionKey: 'SIDENAV.DESC_LEADERS',
    faIcon: ['fas', 'user'],
    primeIcon: 'pi pi-user',
    route: '/dashboard/users',
    allowedRoles: ['Admin', 'Pastor', 'access_usuarios'],
  },
  {
    id: 'offerings',
    labelKey: 'SIDENAV.MENU_FINANCE',
    descriptionKey: 'SIDENAV.DESC_FINANCE',
    faIcon: ['fas', 'money-bill'],
    primeIcon: 'pi pi-wallet',
    route: '/dashboard/offerings',
    allowedRoles: ['Admin', 'Pastor', 'Tesorería', 'access_finanzas'],
  },
  {
    id: 'reports',
    labelKey: 'SIDENAV.MENU_REPORTS',
    descriptionKey: 'SIDENAV.DESC_REPORTS',
    faIcon: ['fas', 'print'],
    primeIcon: 'pi pi-print',
    route: '/dashboard/reports',
    allowedRoles: ['Admin', 'Pastor', 'access_reports'],
  },
  {
    id: 'settings',
    labelKey: 'SIDENAV.MENU_SETTINGS',
    descriptionKey: 'SIDENAV.DESC_SETTINGS',
    faIcon: ['fas', 'cog'],
    primeIcon: 'pi pi-cog',
    route: '/dashboard/settings',
    allowedRoles: ['Admin', 'Pastor'],
  },
  {
    id: 'tutorials',
    labelKey: 'SIDENAV.MENU_TUTORIALS',
    descriptionKey: 'SIDENAV.DESC_TUTORIALS',
    faIcon: ['fas', 'circle-play'],
    primeIcon: 'pi pi-youtube',
    route: '/dashboard/tutoriales',
    allowedRoles: ['*'],
    requiresTutorials: true,
  },
  {
    id: 'support',
    labelKey: 'SIDENAV.MENU_SUPPORT',
    descriptionKey: 'SIDENAV.DESC_SUPPORT',
    faIcon: ['fas', 'headset'],
    primeIcon: 'pi pi-headphones',
    route: '/dashboard/support',
    allowedRoles: ['*'],
  },
];

export type NavGroupId = 'home' | 'people' | 'finance' | 'help' | 'settings';

export interface AppNavGroup {
  id: NavGroupId;
  /** Clave ngx-translate (SIDENAV.GROUP_*) */
  labelKey: string;
  primeIcon: string;
  itemIds: readonly NavId[];
}

/** Secciones del menú superior, en orden de aparición. */
export const APP_NAV_GROUPS: readonly AppNavGroup[] = [
  { id: 'home', labelKey: 'SIDENAV.GROUP_HOME', primeIcon: 'pi pi-home', itemIds: ['dashboard'] },
  { id: 'people', labelKey: 'SIDENAV.GROUP_PEOPLE', primeIcon: 'pi pi-users', itemIds: ['members', 'users'] },
  { id: 'finance', labelKey: 'SIDENAV.GROUP_FINANCE', primeIcon: 'pi pi-wallet', itemIds: ['offerings', 'reports'] },
  { id: 'help', labelKey: 'SIDENAV.GROUP_HELP', primeIcon: 'pi pi-question-circle', itemIds: ['tutorials', 'support'] },
  { id: 'settings', labelKey: 'SIDENAV.GROUP_SETTINGS', primeIcon: 'pi pi-cog', itemIds: ['settings'] },
];

export interface VisibleNavGroup extends AppNavGroup {
  items: AppNavItem[];
}

/** Secciones con al menos una opción permitida; cada una solo con sus opciones permitidas. */
export function visibleNavGroups(ctx: NavContext): VisibleNavGroup[] {
  return APP_NAV_GROUPS
    .map(group => ({
      ...group,
      items: group.itemIds
        .filter(id => canOpenNav(id, ctx))
        .map(id => findNavItem(id)!),
    }))
    .filter(group => group.items.length > 0);
}

export function hasAnyRole(roles: readonly string[] | null | undefined, allowedRoles: readonly string[]): boolean {
  if (allowedRoles.includes('*')) return true;
  return (roles ?? []).some(role => allowedRoles.includes(role));
}

export function findNavItem(navId: string): AppNavItem | undefined {
  return APP_NAV_ITEMS.find(item => item.id === navId);
}

export function canOpenNav(navId: string, ctx: NavContext): boolean {
  const item = findNavItem(navId);
  if (!item) return false;
  if (item.requiresTutorials && !ctx.tutorialsEnabled) return false;
  return hasAnyRole(ctx.roles, item.allowedRoles);
}

export function visibleNavItems(ctx: NavContext): AppNavItem[] {
  return APP_NAV_ITEMS.filter(item => canOpenNav(item.id, ctx));
}

/** Segmentos de una URL sin query ni fragmento. */
export function routeSegments(url: string): string[] {
  const path = url.split(/[?#]/)[0];
  return path.split('/').filter(Boolean);
}

/**
 * Compara una URL con un patrón por segmentos. Los segmentos `:param` aceptan cualquier valor.
 * `/dashboard/members` no coincide con `/dashboard/members-old`.
 */
export function matchesRoute(url: string, pattern: string): boolean {
  const actual = routeSegments(url);
  const expected = routeSegments(pattern);
  if (actual.length !== expected.length) return false;
  return expected.every((segment, i) => segment.startsWith(':') || segment === actual[i]);
}
